import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {App, Button, Checkbox, DatePicker, InputNumber, Modal, Segmented, Spin, Switch, Table, Tabs, Tag} from 'antd';
import dayjs, {type Dayjs} from 'dayjs';
import {HedgePositionCard} from './components/HedgePositionCard';
import {HedgeHistoryCard} from './components/HedgeHistoryCard';
import {HedgeLegChartModal} from './components/HedgeLegChartModal';
import {HedgeReportModal} from './components/HedgeReportModal';
import {HedgeReportDrawer} from './components/HedgeReportDrawer';
import {
    closeHedgePosition,
    getHedgeActiveStats,
    getHedgeConfig,
    getHedgeExecutions,
    getHedgeHistoryPositions,
    getHedgeHistoryStats,
    getHedgePositions,
    getUserHedgeConfig,
    parseSymbolList,
    rebalanceHedgePosition,
    retryHedgePosition,
    updateHedgeConfig,
    updateUserHedgeConfig,
} from '../../services/hedgeService';
import type {
    HedgeActiveStats,
    HedgeConfig,
    HedgeExecution,
    HedgeHistoryQuery,
    HedgeHistoryStats,
    HedgePosition,
    UserHedgeConfig,
} from '../../types/hedge';
import './HedgePage.css';

const {RangePicker} = DatePicker;

const SYMBOLS = ['BTC', 'ETH', 'SOL', 'SUI', 'ADA', 'XRP'];

/** 结束 tab 瀑布流每页条数 */
const HISTORY_PAGE_SIZE = 20;

/** 执行状态中文与颜色（CALCULATED/SKIPPED/REJECTED/EXECUTING/OPEN/PARTIAL_OPEN/COMPENSATED/FAILED/NEEDS_MANUAL） */
const EXEC_STATUS_META: Record<string, { text: string; color: string }> = {
    CALCULATED: {text: '待执行', color: 'blue'},
    SKIPPED: {text: '未触发', color: 'default'},
    REJECTED: {text: '风控拒绝', color: 'orange'},
    EXECUTING: {text: '执行中', color: 'processing'},
    OPEN: {text: '已完成对冲', color: 'green'},
    PARTIAL_OPEN: {text: '部分对冲', color: 'gold'},
    COMPENSATED: {text: '已补偿退出', color: 'volcano'},
    FAILED: {text: '失败', color: 'red'},
    NEEDS_MANUAL: {text: '需人工', color: 'red'},
};

/** 结束态（无敞口）：平仓完成/补偿退出/无敞口失败；其余（含平仓残留、需人工）均视为运行中 */
const END_STATUSES = ['CLOSED', 'COMPENSATED', 'FAILED'];

/**
 * 后端时间统一为 yyyy-MM-dd HH:mm:ss；兼容 ISO(含微秒小数)、时间戳等形态，
 * 避免部分浏览器对非标准格式解析为 Invalid Date
 */
const fmtTime = (value?: string | number | null): string => {
    if (null == value || '' === value) {
        return '--';
    }
    const text = String(value).trim().replace('T', ' ').replace(/\.\d+$/, '').slice(0, 19);
    const parsed = dayjs(text);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '--';
};

/**
 * 对冲交易管理页（/hedge）
 * 配置面板（开关/自动执行/允许方向/比例/金额）+ 汇总统计 + 双腿卡片区（完成度三态）+
 * 历史 + 执行/审核记录；
 * 10 秒轮询刷新，异常卡片标红并提供补单/重试/平仓手动干预。
 */
export const HedgePage: React.FC = () => {
    const {message, modal} = App.useApp();
    const [config, setConfig] = useState<HedgeConfig | null>(null);
    /** 我的品种配置（报告可见 + 对冲品种 + 对冲参数） */
    const [userConfig, setUserConfig] = useState<UserHedgeConfig | null>(null);
    /** 已勾选的对冲品种（保存写 hedgesAllowedSymbols） */
    const [hedgeSymbols, setHedgeSymbols] = useState<string[]>([]);
    const [hedgeSymbolModal, setHedgeSymbolModal] = useState(false);
    const [positions, setPositions] = useState<HedgePosition[]>([]);
    /** 运行中 tab 统计行（后端聚合；与卡片同为 10s 轮询刷新） */
    const [activeStats, setActiveStats] = useState<HedgeActiveStats | null>(null);
    const [executions, setExecutions] = useState<HedgeExecution[]>([]);
    const [symbol, setSymbol] = useState('BTC');
    const [activePositionId, setActivePositionId] = useState<number | null>(null);
    const [chartTarget, setChartTarget] =
        useState<{ position: HedgePosition; market: 'SPOT' | 'SWAP'; ended: boolean } | null>(null);
    const [reportModalId, setReportModalId] = useState<number | null>(null);
    /** 结束态卡片点击 → 右侧研报抽屉的目标持仓 */
    const [reportTarget, setReportTarget] = useState<HedgePosition | null>(null);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [ratio, setRatio] = useState<number>(70);
    const [amount, setAmount] = useState<number>(1000);
    const [directionMode, setDirectionMode] = useState<UserHedgeConfig['directionMode']>('BOTH');

    const loadConfig = useCallback(async () => {
        try {
            setConfig(await getHedgeConfig());
        } catch {
            setConfig(null);
        }
        try {
            const user = await getUserHedgeConfig();
            setUserConfig(user);
            if (null != user) {
                setRatio(Number(user.hedgeRatio));
                setAmount(Number(user.fixedAmountUsdt));
                setDirectionMode(user.directionMode ?? 'BOTH');
                setHedgeSymbols(parseSymbolList(user.hedgesAllowedSymbols));
            }
        } catch {
            setUserConfig(null);
        }
    }, []);

    const loadPositions = useCallback(async () => {
        try {
            setPositions(await getHedgePositions());
        } catch {
            setPositions([]);
        }
    }, []);

    const loadActiveStats = useCallback(async () => {
        try {
            setActiveStats(await getHedgeActiveStats());
        } catch {
            setActiveStats(null);
        }
    }, []);

    const loadExecutions = useCallback(async (sym: string) => {
        try {
            const page = await getHedgeExecutions(sym, 0, 20);
            setExecutions(page?.content ?? []);
        } catch {
            setExecutions([]);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadConfig(), loadPositions(), loadActiveStats()]).finally(() => setLoading(false));
    }, [loadConfig, loadPositions, loadActiveStats]);

    useEffect(() => {
        loadExecutions(symbol);
    }, [symbol, loadExecutions]);

    // 10 秒轮询：持仓、运行中统计与执行记录
    useEffect(() => {
        const timer = setInterval(() => {
            loadPositions();
            loadActiveStats();
            loadExecutions(symbol);
        }, 10000);
        return () => clearInterval(timer);
    }, [symbol, loadPositions, loadActiveStats, loadExecutions]);

    const handleToggle = async (checked: boolean) => {
        try {
            const updated = await updateHedgeConfig({enabled: checked ? 1 : 0});
            setConfig(updated);
            message.success(checked ? '对冲策略已开启' : '对冲策略已关闭');
        } catch (e: any) {
            message.error(e?.message ?? '开关更新失败');
        }
    };

    const handleSave = async () => {
        try {
            const updated = await updateUserHedgeConfig({
                directionMode,
                hedgeRatio: ratio,
                fixedAmountUsdt: amount,
            });
            setUserConfig(updated);
            message.success('策略参数已保存');
        } catch (e: any) {
            message.error(e?.message ?? '保存失败');
        }
    };

    const persistAutoExecute = async (checked: boolean) => {
        try {
            // 自动执行切换与当前表单值一起保存，避免用尚未保存的旧方向启动真实交易。
            const updated = await updateUserHedgeConfig({
                autoExecute: checked ? 1 : 0,
                directionMode,
                hedgeRatio: ratio,
                fixedAmountUsdt: amount,
            });
            setUserConfig(updated);
            message.success(checked ? '自动执行已开启' : '自动执行已关闭');
        } catch (e: any) {
            message.error(e?.message ?? '自动执行开关更新失败');
        }
    };

    /** 保存对冲品种（选项 = 我的报告可见品种；后端二次校验 ⊆ 报告配置，越界拒绝） */
    const saveHedgeSymbols = async () => {
        try {
            const updated = await updateUserHedgeConfig({hedgesAllowedSymbols: hedgeSymbols.join(',')});
            setUserConfig(updated);
            setHedgeSymbols(parseSymbolList(updated?.hedgesAllowedSymbols));
            setHedgeSymbolModal(false);
            message.success('对冲品种已保存');
        } catch (e: any) {
            message.error(e?.message ?? '对冲品种保存失败');
        }
    };

    const handleAutoExecuteToggle = (checked: boolean) => {
        if (!checked) {
            void persistAutoExecute(false);
            return;
        }
        modal.confirm({
            title: '确认开启自动执行？',
            content: '开启后，新研报符合方向、持仓和风控条件时将自动提交真实交易，无需人工点击。',
            okText: '确认开启',
            cancelText: '取消',
            onOk: () => persistAutoExecute(true),
        });
    };

    const runAction = async (action: (id: number) => Promise<unknown>, id: number, label: string) => {
        setBusyId(id);
        try {
            await action(id);
            message.success(`${label}完成`);
        } catch (e: any) {
            // 未完成/失败：使用后端返回的真实原因（如"平仓未完成（状态 X）"）
            message.error(e?.message ?? `${label}失败`);
        } finally {
            // 成功与失败都刷新，避免卡片与统计停留在与服务端不一致的状态
            await Promise.all([loadPositions(), loadActiveStats()]);
            // 平仓/重试可能使记录转入结束态：标记结束 tab 数据过期，切回时重置拉取
            setHistoryDirty(true);
            setBusyId(null);
        }
    };

    // 激活项被平仓/移出列表后清除高亮（品种仍保留，避免执行记录被意外切走）
    useEffect(() => {
        if (null != activePositionId && !positions.some(p => p.id === activePositionId)) {
            setActivePositionId(null);
        }
    }, [positions, activePositionId]);

    /** 点击卡片：激活该 Item，并把下方执行记录切到该品种 */
    const handleActivate = (p: HedgePosition) => {
        setActivePositionId(p.id);
        if (p.symbol !== symbol) {
            setSymbol(p.symbol);
        }
    };

    /** 手动切品种：与当前激活项品种不一致时取消激活高亮 */
    const handleSymbolChange = (value: string | number) => {
        const next = String(value);
        setSymbol(next);
        const activePosition = positions.find(p => p.id === activePositionId);
        if (activePosition && activePosition.symbol !== next) {
            setActivePositionId(null);
        }
    };

    const activePositions = positions.filter(p => !END_STATUSES.includes(p.status));

    // ══ 结束 tab：后端筛选分页（瀑布流）与命中统计 ══
    const [historyStatus, setHistoryStatus] = useState<string>('ALL');
    const [historySymbol, setHistorySymbol] = useState<string>('ALL');
    const [historyRange, setHistoryRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [historyItems, setHistoryItems] = useState<HedgePosition[]>([]);
    const [historyPage, setHistoryPage] = useState(-1);
    const [historyHasMore, setHistoryHasMore] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyStats, setHistoryStats] = useState<HedgeHistoryStats | null>(null);
    /** 数据变更标记（平仓/重试动作后置位，切回结束 tab 时重置拉取） */
    const [historyDirty, setHistoryDirty] = useState(true);
    const [activeTab, setActiveTab] = useState('active');
    /** 翻页加载锁：state 更新异步，用 ref 防 IntersectionObserver 连续触发重复请求 */
    const historyLoadingRef = useRef(false);
    /** 请求序号：重置加载时自增，使在途旧响应失效 */
    const historySeqRef = useRef(0);

    /** 品种筛选选项：默认品种 + 运行中/已加载历史出现过的品种 */
    const historySymbolOptions = useMemo(() => {
        const seen = new Set<string>(SYMBOLS);
        positions.forEach(p => p.symbol && seen.add(p.symbol));
        historyItems.forEach(p => p.symbol && seen.add(p.symbol));
        return [{label: '全部', value: 'ALL'}, ...Array.from(seen).map(s => ({label: s, value: s}))];
    }, [positions, historyItems]);

    /** 当前筛选 → 后端查询参数（空值不传；时间含整天） */
    const historyQuery = useMemo((): HedgeHistoryQuery => {
        const query: HedgeHistoryQuery = {};
        if ('ALL' !== historyStatus) {
            query.status = historyStatus;
        }
        if ('ALL' !== historySymbol) {
            query.symbol = historySymbol;
        }
        if (null != historyRange) {
            query.from = historyRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
            query.to = historyRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
        }
        return query;
    }, [historyStatus, historySymbol, historyRange]);

    /** 重置并加载第一页与统计（筛选变化/切 tab/平仓动作后） */
    const reloadHistory = useCallback(async (query: HedgeHistoryQuery) => {
        const seq = ++historySeqRef.current;
        historyLoadingRef.current = true;
        setHistoryLoading(true);
        try {
            const [pageData, stats] = await Promise.all([
                getHedgeHistoryPositions(query, 0, HISTORY_PAGE_SIZE),
                getHedgeHistoryStats(query),
            ]);
            if (seq !== historySeqRef.current) {
                return;
            }
            setHistoryItems(pageData.content);
            setHistoryPage(0);
            setHistoryHasMore(pageData.number + 1 < pageData.totalPages);
            setHistoryStats(stats);
            setHistoryDirty(false);
        } catch {
            if (seq !== historySeqRef.current) {
                return;
            }
            setHistoryItems([]);
            setHistoryPage(0);
            setHistoryHasMore(false);
            setHistoryStats(null);
        } finally {
            if (seq === historySeqRef.current) {
                historyLoadingRef.current = false;
                setHistoryLoading(false);
            }
        }
    }, []);

    /** 瀑布流：加载下一页并追加 */
    const loadMoreHistory = useCallback(async () => {
        if (historyLoadingRef.current || !historyHasMore || 0 > historyPage) {
            return;
        }
        const seq = historySeqRef.current;
        historyLoadingRef.current = true;
        setHistoryLoading(true);
        try {
            const pageData = await getHedgeHistoryPositions(historyQuery, historyPage + 1, HISTORY_PAGE_SIZE);
            if (seq !== historySeqRef.current) {
                return;
            }
            setHistoryItems(prev => [...prev, ...pageData.content]);
            setHistoryPage(pageData.number);
            setHistoryHasMore(pageData.number + 1 < pageData.totalPages);
        } catch {
            if (seq === historySeqRef.current) {
                // 停止自动触底；置脏使切回本 tab 时重置恢复
                setHistoryHasMore(false);
                setHistoryDirty(true);
            }
        } finally {
            if (seq === historySeqRef.current) {
                historyLoadingRef.current = false;
                setHistoryLoading(false);
            }
        }
    }, [historyHasMore, historyPage, historyQuery]);

    /** 跳过筛选 effect 首次挂载触发（初始数据由切 tab/首载统计负责） */
    const historyFilterInitRef = useRef(true);

    // 筛选变化 → 重置加载第一页与统计（筛选器只在结束 tab 内，变化时必在该 tab）
    useEffect(() => {
        if (historyFilterInitRef.current) {
            historyFilterInitRef.current = false;
            return;
        }
        void reloadHistory(historyQuery);
    }, [historyQuery, reloadHistory]);

    // 首载统计（顶部汇总"历史"数 + 结束 tab 计数；已加载过则不覆盖）
    useEffect(() => {
        getHedgeHistoryStats({}).then(s => setHistoryStats(prev => prev ?? s)).catch(() => undefined);
    }, []);

    // 切到结束 tab：未加载或数据变更后重置拉取
    useEffect(() => {
        if ('history' === activeTab && (historyDirty || 0 > historyPage) && !historyLoadingRef.current) {
            void reloadHistory(historyQuery);
        }
        // 依赖不含 historyQuery：筛选变化已由上方 effect 处理，避免重复加载
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, historyDirty, historyPage, reloadHistory]);

    /** 瀑布流加载哨兵（进入视口提前 120px 加载下一页） */
    const historySentinelRef = useRef<HTMLDivElement | null>(null);
    const loadMoreRef = useRef<() => void>(() => undefined);
    loadMoreRef.current = () => {
        if ('history' !== activeTab) {
            return;
        }
        void loadMoreHistory();
    };
    useEffect(() => {
        const sentinel = historySentinelRef.current;
        if (null == sentinel) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                loadMoreRef.current();
            }
        }, {rootMargin: '120px'});
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [activeTab, loading]);

    /** 统计行盈亏格式化（USDT，盈利带 + 号） */
    const fmtSignedUsdt = (value: number): string =>
        `${0 <= value ? '+' : ''}${Number(value).toFixed(2)}U`;
    const pnlCls = (value: number): string => 0 <= value ? 'ok' : 'warn';
    // 统计行数据（后端命中统计；null=无数据展示 --）
    const statsCount = historyStats?.count ?? 0;
    const statsPnlSum = historyStats?.pnlSum;
    const statsPnlCount = historyStats?.pnlCount ?? 0;
    const statsInvested = Number(historyStats?.investedSum ?? 0);
    const statsAvg = null == statsPnlSum || 0 === statsPnlCount ? null : Number(statsPnlSum) / statsPnlCount;
    /** 盈亏率（%）：总盈亏 ÷ 总投入（含建仓失败名义投入） */
    const statsPnlRate = null == statsPnlSum || 0 === statsInvested
        ? null : Number(statsPnlSum) / statsInvested * 100;
    const statsMaxSymbol = historyStats?.maxSymbol;
    const statsMinSymbol = historyStats?.minSymbol;
    const maxSymbol: [string, number] | null = null == statsMaxSymbol
        ? null : [statsMaxSymbol, Number(historyStats?.maxSymbolPnl ?? 0)];
    const minSymbol: [string, number] | null = null == statsMinSymbol
        ? null : [statsMinSymbol, Number(historyStats?.minSymbolPnl ?? 0)];
    // 运行中统计行数据（后端 /positions/stats；null=无数据展示 --）
    const activeStatsCount = activeStats?.count ?? 0;
    const activePnlSum = activeStats?.pnlSum;
    const activePnlCount = activeStats?.pnlCount ?? 0;
    const activeInvested = Number(activeStats?.investedSum ?? 0);
    const activeAvg = null == activePnlSum || 0 === activePnlCount
        ? null : Number(activePnlSum) / activePnlCount;
    /** 浮盈亏率（%）：总浮盈亏 ÷ 总投入 */
    const activePnlRate = null == activePnlSum || 0 === activeInvested
        ? null : Number(activePnlSum) / activeInvested * 100;
    const activeMaxPnl = Number(activeStats?.maxSymbolPnl ?? 0);
    const activeMinPnl = Number(activeStats?.minSymbolPnl ?? 0);
    /** 筛选是否激活（空态文案区分"无记录"与"无命中"） */
    const historyFilterActive = 'ALL' !== historyStatus || 'ALL' !== historySymbol || null != historyRange;
    const totalInvested = activePositions.reduce((sum, p) => sum + Number(p.totalUsdt ?? 0), 0);
    const unhedgedCount = activePositions.filter(p => Number(p.unhedgedQty ?? 0) > 0).length;
    const abnormalCount = activePositions.filter(
        p => 'INCOMPLETE' === p.completionState || 'NEEDS_MANUAL' === p.status).length;
    // 默认品种 + 当前持仓品种（避免点击非默认品种后 Segmented 显示空白）
    const symbolOptions = useMemo(() => {
        const merged = new Set(SYMBOLS);
        positions.forEach(p => p.symbol && merged.add(p.symbol));
        return Array.from(merged).map(s => ({label: s, value: s}));
    }, [positions]);
    // K 线弹窗取最新持仓对象，保证 10s 轮询的实时价/均价能刷新到弹窗；持仓移出列表时回退打开时快照
    const chartPosition = chartTarget
        ? positions.find(p => p.id === chartTarget.position.id) ?? chartTarget.position
        : null;

    const renderCard = (p: HedgePosition) => (
        <HedgePositionCard
            key={p.id}
            position={p}
            busy={busyId === p.id}
            active={activePositionId === p.id}
            onActivate={handleActivate}
            onClose={(id) => runAction(closeHedgePosition, id, '平仓')}
            onRebalance={(id) => runAction(rebalanceHedgePosition, id, '补单')}
            onRetry={(id) => runAction(retryHedgePosition, id, '重试')}
            onOpenChart={(position, market) => setChartTarget({position, market, ended: false})}
        />
    );

    return (
        <div className="hedge-page">
            <div className="hedge-toolbar">
                <span className="hedge-title">对冲交易管理</span>
                <span className="hedge-conf-label">策略开关</span>
                <Switch checked={1 === Number(config?.enabled)} onChange={handleToggle}/>
                <span className="hedge-conf-label">自动执行</span>
                <Switch checked={1 === Number(userConfig?.autoExecute)} onChange={handleAutoExecuteToggle}/>
                <span className="hedge-conf-label">允许方向</span>
                <Segmented
                    size="small"
                    value={directionMode}
                    options={[
                        {label: '双向', value: 'BOTH'},
                        {label: '仅偏多', value: 'BULLISH_ONLY'},
                        {label: '仅偏空', value: 'BEARISH_ONLY'},
                    ]}
                    onChange={(value) => setDirectionMode(value as UserHedgeConfig['directionMode'])}
                />
                <span className="hedge-conf-label">对冲比例</span>
                <InputNumber value={ratio} min={1} max={100} step={5} style={{width: 80}}
                             onChange={(v) => setRatio(Number(v ?? 70))}/>
                <span className="hedge-conf-label">单品种金额(U)</span>
                <InputNumber value={amount} min={5} step={100} style={{width: 110}}
                             onChange={(v) => setAmount(Number(v ?? 1000))}/>
                <Button size="small" type="primary" onClick={handleSave}>保存</Button>
                <Button size="small" onClick={() => setHedgeSymbolModal(true)}>
                    对冲品种（{hedgeSymbols.length}）
                </Button>
            </div>

            <div className="hedge-summary">
                <span>进行中 <b>{activePositions.length}</b></span>
                <span>总投入 <b>{totalInvested.toFixed(2)} U</b></span>
                <span className={unhedgedCount > 0 ? 'warn' : ''}>敞口 <b>{unhedgedCount}</b></span>
                <span className={abnormalCount > 0 ? 'warn' : ''}>异常 <b>{abnormalCount}</b></span>
                <span>历史 <b>{historyStats?.totalCount ?? 0}</b></span>
            </div>

            {loading ? (
                <div className="hedge-loading"><Spin/></div>
            ) : (
                <Tabs
                    className="hedge-tabs"
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'active',
                            label: `运行中（${activePositions.length}）`,
                            children: (
                                <div className="hedge-active-panel">
                                    <div className="hedge-active-stats">
                                        <span>持仓 <b>{activeStatsCount}</b></span>
                                        <span className={null == activePnlSum ? 'dim' : pnlCls(activePnlSum)}>
                                            总浮盈亏 <b>{null == activePnlSum ? '--' : fmtSignedUsdt(activePnlSum)}</b>
                                        </span>
                                        <span>总投入 <b>{activeInvested.toFixed(2)} U</b></span>
                                        <span className={null == activePnlRate ? 'dim' : pnlCls(activePnlRate)}>
                                            浮盈亏率 <b>{null == activePnlRate
                                            ? '--' : `${0 <= activePnlRate ? '+' : ''}${activePnlRate.toFixed(2)}%`}</b>
                                        </span>
                                        <span className={null == activeAvg ? 'dim' : pnlCls(activeAvg)}>
                                            平均浮盈亏 <b>{null == activeAvg ? '--' : fmtSignedUsdt(activeAvg)}</b>
                                        </span>
                                        <span className={null == activeStats?.maxSymbol ? 'dim' : pnlCls(activeMaxPnl)}>
                                            最高 <b>{null == activeStats?.maxSymbol
                                            ? '--' : `${activeStats.maxSymbol} ${fmtSignedUsdt(activeMaxPnl)}`}</b>
                                        </span>
                                        <span className={null == activeStats?.minSymbol ? 'dim' : pnlCls(activeMinPnl)}>
                                            最低 <b>{null == activeStats?.minSymbol
                                            ? '--' : `${activeStats.minSymbol} ${fmtSignedUsdt(activeMinPnl)}`}</b>
                                        </span>
                                    </div>
                                    <div className="hedge-grid">
                                        {activePositions.map(renderCard)}
                                        {!activePositions.length &&
                                            <div className="hedge-empty">暂无运行中的对冲持仓</div>}
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: 'history',
                            label: `结束（${statsCount}）`,
                            children: (
                                <div className="hedge-history-panel">
                                    <div className="hedge-history-filter">
                                        <span className="hedge-conf-label">状态</span>
                                        <Segmented
                                            size="small"
                                            value={historyStatus}
                                            options={[
                                                {label: '全部', value: 'ALL'},
                                                {label: '已平仓', value: 'CLOSED'},
                                                {label: '补偿退出', value: 'COMPENSATED'},
                                                {label: '建仓失败', value: 'FAILED'},
                                            ]}
                                            onChange={(v) => setHistoryStatus(String(v))}
                                        />
                                        <span className="hedge-conf-label">品种</span>
                                        <Segmented
                                            size="small"
                                            value={historySymbol}
                                            options={historySymbolOptions}
                                            onChange={(v) => setHistorySymbol(String(v))}
                                        />
                                        <span className="hedge-conf-label">建仓时间</span>
                                        <RangePicker
                                            size="small"
                                            value={historyRange}
                                            allowClear
                                            onChange={(dates) => {
                                                if (null == dates || null == dates[0] || null == dates[1]) {
                                                    setHistoryRange(null);
                                                    return;
                                                }
                                                setHistoryRange([dates[0], dates[1]]);
                                            }}
                                        />
                                    </div>

                                    <div className="hedge-history-stats">
                                        <span>命中 <b>{statsCount}</b></span>
                                        <span className={null == statsPnlSum ? 'dim' : pnlCls(statsPnlSum)}>
                                            总盈亏 <b>{null == statsPnlSum ? '--' : fmtSignedUsdt(statsPnlSum)}</b>
                                        </span>
                                        <span>总投入 <b>{statsInvested.toFixed(2)} U</b></span>
                                        <span className={null == statsPnlRate ? 'dim' : pnlCls(statsPnlRate)}>
                                            盈亏率 <b>{null == statsPnlRate
                                            ? '--' : `${0 <= statsPnlRate ? '+' : ''}${statsPnlRate.toFixed(2)}%`}</b>
                                        </span>
                                        <span className={null == statsAvg ? 'dim' : pnlCls(statsAvg)}>
                                            平均盈亏 <b>{null == statsAvg ? '--' : fmtSignedUsdt(statsAvg)}</b>
                                        </span>
                                        <span className={null == maxSymbol ? 'dim' : pnlCls(maxSymbol[1])}>
                                            最高 <b>{null == maxSymbol
                                            ? '--' : `${maxSymbol[0]} ${fmtSignedUsdt(maxSymbol[1])}`}</b>
                                        </span>
                                        <span className={null == minSymbol ? 'dim' : pnlCls(minSymbol[1])}>
                                            最低 <b>{null == minSymbol
                                            ? '--' : `${minSymbol[0]} ${fmtSignedUsdt(minSymbol[1])}`}</b>
                                        </span>
                                    </div>

                                    <div className="hedge-grid">
                                        {historyItems.map(p => (
                                            <HedgeHistoryCard key={p.id} position={p}
                                                              onOpenReport={setReportTarget}
                                                              onOpenChart={(position, market) =>
                                                                  setChartTarget({position, market, ended: true})}/>
                                        ))}
                                        {!historyLoading && 0 === historyItems.length && -1 !== historyPage && (
                                            <div className="hedge-empty">
                                                {historyFilterActive ? '暂无符合筛选条件的记录' : '暂无已结束的对冲持仓'}
                                            </div>
                                        )}
                                    </div>
                                    <div ref={historySentinelRef} className="hedge-history-sentinel">
                                        {historyLoading && <Spin size="small"/>}
                                        {!historyLoading && !historyHasMore && 0 < historyItems.length && (
                                            <span className="hedge-history-end">已加载全部</span>
                                        )}
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />
            )}

            <div className="hedge-section-title">执行记录</div>
            <Segmented options={symbolOptions} value={symbol} onChange={handleSymbolChange}/>
            <Table<HedgeExecution>
                className="hedge-exec-table"
                dataSource={executions}
                rowKey="id"
                size="small"
                pagination={false}
                rowClassName="hedge-exec-row"
                onRow={(record) => ({
                    onClick: () => {
                        if (null != record.reportId) {
                            setReportModalId(record.reportId);
                        }
                    },
                })}
                locale={{emptyText: '暂无执行记录'}}
                columns={[
                    {
                        title: '研报', dataIndex: 'reportId', width: 100,
                        render: (v: number) => (
                            <span className="hedge-exec-report-link">{null == v ? '--' : `#${v}`}</span>
                        ),
                    },
                    {
                        title: '方向', dataIndex: 'bias', width: 80,
                        render: (v: string) => 'bullish' === v
                            ? <span className="bull">▲偏多</span>
                            : ('bearish' === v ? <span className="bear">▼偏空</span> : '--'),
                    },
                    {
                        title: '状态', dataIndex: 'execStatus', width: 110,
                        render: (v: string) => {
                            const meta = EXEC_STATUS_META[v] ?? {text: v, color: 'default'};
                            return <Tag color={meta.color}>{meta.text}</Tag>;
                        },
                    },
                    {
                        title: '触发', dataIndex: 'submitMode', width: 80,
                        render: (v?: string) => 'AUTO' === v ? '自动' : ('MANUAL' === v ? '手动' : '--'),
                    },
                    {
                        title: '交易对', key: 'pair',
                        render: (_, record) => {
                            const pair = record.pair;
                            if (null == pair) {
                                return record.skipReason ?? '--';
                            }
                            const spotIsMain = 'SPOT' === pair.mainLeg;
                            return (
                                <span className="hedge-exec-pair">
                                    {spotIsMain ? '主单 买现货' : '主单 空永续'}
                                    {' + '}
                                    {spotIsMain ? '对冲 空永续' : '对冲 买现货'}
                                    {`（${Number(pair.hedgeRatio)}%）`}
                                </span>
                            );
                        },
                    },
                    {
                        title: '风控审核', key: 'audit', width: 260,
                        render: (_, record) => {
                            if (null == record.auditPass) {
                                return <span className="dim">未审核</span>;
                            }
                            return (
                                <span className={1 === Number(record.auditPass) ? 'ok' : 'warn'}>
                                    {1 === Number(record.auditPass) ? '通过' : '拒绝'}：{record.auditReason ?? '--'}
                                </span>
                            );
                        },
                    },
                    {
                        title: '时间', dataIndex: 'createdAt', width: 160,
                        render: (v: string) => fmtTime(v),
                    },
                ]}
            />

            <HedgeLegChartModal
                visible={null != chartTarget}
                position={chartPosition}
                initialMarket={chartTarget?.market ?? 'SPOT'}
                ended={chartTarget?.ended ?? false}
                onClose={() => setChartTarget(null)}
            />
            <HedgeReportModal
                open={null != reportModalId}
                reportId={reportModalId}
                onClose={() => setReportModalId(null)}
            />
            <HedgeReportDrawer
                open={null != reportTarget}
                reportId={reportTarget?.reportId ?? null}
                symbol={reportTarget?.symbol}
                onClose={() => setReportTarget(null)}
            />
            <Modal
                title="对冲品种配置"
                open={hedgeSymbolModal}
                onOk={saveHedgeSymbols}
                onCancel={() => setHedgeSymbolModal(false)}
                okText="保存"
                cancelText="取消"
            >
                <div style={{marginBottom: 12, color: 'var(--pt-text-secondary, #999)', fontSize: 13}}>
                    仅可选择「我的报告品种配置」中已勾选可见的品种（没有报告就不会触发对冲）；
                    未勾选的品种不会自动对冲，仅保留运行中持仓的监控与保护。
                </div>
                {0 === parseSymbolList(userConfig?.reportsAllowedSymbols).length ? (
                    <div className="hedge-empty">
                        暂无可选品种，请先到「研报页 → 品种配置」勾选可见报告品种
                    </div>
                ) : (
                    <Checkbox.Group
                        value={hedgeSymbols}
                        options={parseSymbolList(userConfig?.reportsAllowedSymbols).map(s => ({label: s, value: s}))}
                        onChange={(values) => setHedgeSymbols(values as string[])}
                    />
                )}
            </Modal>
        </div>
    );
};

export default HedgePage;
